import { Button } from '@1trading/react/components/ui/button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, type To } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCardFooter } from '@/components/ui/page-card';
import { InstitutionOnboardingProgressStep } from '@/context/progress';
import { useDetailsNext } from '@/features/institution/hooks/useDetailsNext';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { VerificationProps } from '@/features/verification/components';
import Verification from '@/features/verification/components';
import VerificationLayout from '@/features/verification/layout';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileInstitutionDetailsAnswers } from '@/hooks/queries/use-profile-institution-details';
import { useProfilePrivacy } from '@/hooks/queries/use-profile-privacy';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';

type InstitutionVerificationProps = Extract<VerificationProps, { next: To }>;

const DEFAULT_LAYOUT_PROPS: VerificationProps['layoutProps'] = {
  currentStep: InstitutionOnboardingProgressStep.INSTITUTION_VERIFICATION,
};

const InstitutionVerificationLoading = () => {
  return (
    <VerificationLayout {...DEFAULT_LAYOUT_PROPS}>
      <LoadingCard />
    </VerificationLayout>
  );
};
InstitutionVerificationLoading.displayName = 'InstitutionVerificationLoading';

const InstitutionVerification = ({ next, layoutProps, sdkConfig, ...props }: InstitutionVerificationProps) => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsNext, data: institutionDetailsNext } = useDetailsNext(INSTITUTION_DETAILS_TYPE.ONBOARDING);
  const { isLoading: isLoadingPrivacy } = useProfilePrivacy();
  const { isLoading: isLoadingVerification, set: setVerificationInformation } = useProfileVerificationInformation();
  const { isLoading: isLoadingUserAttributes, set: setUserAttributes } = useUserAttributes();
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const [showContinue, setShowContinue] = useState(false);
  const isLoading =
    isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsNext || isLoadingPrivacy || isLoadingVerification || isLoadingUserAttributes || isLoadingCountries;
  const countryOfIncorporation = institutionDetailsAnswers?.incorporation?.countryOfIncorporation;
  const country = countryOfIncorporation ? countries?.[countryOfIncorporation]?.alpha3 : undefined;

  if (isLoading) return <InstitutionVerificationLoading />;

  if (institutionDetailsNext?.route) return <Navigate to={institutionDetailsNext.route} replace />;

  const handleChange: Extract<VerificationProps, { onChange: VerificationProps['onChange'] }>['onChange'] = (data) => {
    setVerificationInformation({
      verification: {
        level: data.levelName,
        status: 'VERIFICATION_STARTED',
        verification_date: data.creationDate,
      },
    });
    setUserAttributes({ verified: 'started' });
    setShowContinue(true);
  };

  return (
    <Verification layoutProps={{ ...DEFAULT_LAYOUT_PROPS, ...layoutProps }} sdkConfig={{ ...sdkConfig, country }} onChange={handleChange} {...props}>
      {showContinue ? (
        <PageCardFooter>
          <Button className="w-full" size="lg" variant="primary" asChild>
            <Link to={next} replace>
              {t('fundamentals.next')}
            </Link>
          </Button>
        </PageCardFooter>
      ) : null}
    </Verification>
  );
};
InstitutionVerification.displayName = 'InstitutionVerification';

export default InstitutionVerification;
